import React, { useState, useEffect } from 'react';
import './App.css';
import logo from './assets/ctta-logo.avif';
import itaLogo from './assets/ITA_Logo.png';
import Calendar from './Calendar';
import Location from './Location';
import ContactUs from './ContactUs';
import VasanthaVizha from './VasanthaVizha';
import Donation from './Donation';
import TopContactBar from './TopContactBar';
import Footer from './Footer';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminHome from './admin/AdminHome';
import AdminCalendar from './admin/AdminCalendar';
import AdminGameCreate from './admin/AdminGameCreate';
import AdminGameControl from './admin/AdminGameControl';
import AdminGameDisplay from './admin/AdminGameDisplay';
import AdminSessionList from './admin/AdminSessionList';
import AdminQuestionGroups from './admin/AdminQuestionGroups';
import AdminBranchManager from './admin/AdminBranchManager';
import AdminUserManager from './admin/AdminUserManager';
import AdminRolePermissions from './admin/AdminRolePermissions';
import AdminFAQManager from './admin/AdminFAQManager';
import AdminStudentRegistrationHome from './admin/student-registration/AdminStudentRegistrationHome';
import AdminStudentRegistrationSetup from './admin/student-registration/AdminStudentRegistrationSetup';
import AdminStudentRegistrationImport from './admin/student-registration/AdminStudentRegistrationImport';
import AdminStudentRegistrationCheckIn from './admin/student-registration/AdminStudentRegistrationCheckIn';
import AdminStudentRegistrationRoster from './admin/student-registration/AdminStudentRegistrationRoster';
import AdminGradeEvaluation from './admin/student-registration/AdminGradeEvaluation';
import AdminTransferRequests from './admin/AdminTransferRequests';
import AdminVolunteers from './admin/AdminVolunteers';
import AdminMyClass from './admin/AdminMyClass';
import AdminEmailTemplates from './admin/AdminEmailTemplates';
import AdminBulkEmail from './admin/AdminBulkEmail';
import AdminSitePages from './admin/AdminSitePages';
import AdminContactUs from './admin/AdminContactUs';
import Faq from './Faq';
import TransferAccessGate from './transfer/TransferAccessGate';
import GameJoin from './game/GameJoin';
import GamePlay from './game/GamePlay';
import { getToken, removeToken, verifyToken, getBranchTheme, getUserTheme } from './utils/auth';
import { applyTheme, resolveTheme } from './theme';
import { API_URL } from './config';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [gameSession, setGameSession] = useState(null);
  const [gameCode, setGameCode] = useState(null);
  const [transferBranchCode, setTransferBranchCode] = useState(null);
  // Which seasonal nav items (Vasantha Vizha, Join Game, ...) are currently turned on --
  // admin-toggleable, see backend app.py's SEASONAL_PAGE_KEYS. Defaults to everything hidden
  // until the fetch resolves, so a slow/failed request never briefly flashes a disabled page.
  const [sitePages, setSitePages] = useState({});
  const [sitePagesLoaded, setSitePagesLoaded] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const handleNavClick = (page) => {
    setCurrentPage(page);
    window.location.hash = page;
    closeMenu();
  };

  const handleAdminNavigate = (page) => {
    setCurrentPage(page);
    window.location.hash = page;
  };

  const handleLogout = () => {
    removeToken();
    setCurrentPage('admin-login');
    window.location.hash = 'admin-login';
  };

  const handleSessionCreated = (session) => {
    setGameSession(session);
    localStorage.setItem('ctta_admin_game_session', JSON.stringify(session));
    setCurrentPage('admin-game-control');
    window.location.hash = 'admin-game-control';
  };

  const handleNavigateToDisplay = () => {
    setCurrentPage('admin-game-display');
    window.location.hash = 'admin-game-display';
  };

  const handleGameJoin = (code) => {
    setGameCode(code);
    localStorage.setItem('ctta_last_game_code', code);
    setCurrentPage('game-play');
    window.location.hash = 'game-play';
  };

  // Check URL hash on mount and hash changes
  useEffect(() => {
    // Fetch branch theme for public pages (before any login)
    fetch(`${API_URL}/api/branch-config`)
      .then(r => r.json())
      .then(d => {
        const effective = resolveTheme(d.theme, getUserTheme(), null);
        applyTheme(effective);
      })
      .catch(() => applyTheme('purple'));

    fetch(`${API_URL}/api/site-pages`)
      .then(r => r.json())
      .then(d => {
        const byKey = {};
        (d.pages || []).forEach(p => { byKey[p.page_key] = p; });
        setSitePages(byKey);
      })
      .catch(() => {})
      .finally(() => setSitePagesLoaded(true));

    const checkAdminAccess = async () => {
      const hash = window.location.hash.substring(1) || 'home'; // Remove # and default to home

      // Check if accessing the public transfer form for a specific branch
      // (e.g. #transfer/CTTA) -- a new prefix branch, same mechanics as
      // game/<code> below, not a general query-string parser.
      if (hash.startsWith('transfer/')) {
        const branchCode = hash.substring(9).toUpperCase();
        if (branchCode) {
          setTransferBranchCode(branchCode);
          setCurrentPage('transfer-form');
          return;
        }
      }

      // Check if accessing game with code (e.g., #game/TAMIL or #game/ABC123)
      if (hash.startsWith('game/')) {
        const code = hash.substring(5).toUpperCase(); // Extract code after 'game/'
        if (code && code.length >= 3 && code.length <= 10) {
          setGameCode(code);
          setCurrentPage('game-play');
          return;
        } else {
          // Invalid code, redirect to game-join
          setCurrentPage('game-join');
          window.location.hash = 'game-join';
          return;
        }
      }

      // Check if accessing admin routes
      if (hash === 'admin' || hash.startsWith('admin-')) {
        const token = getToken();
        if (!token) {
          setCurrentPage('admin-login');
          window.location.hash = 'admin-login';
        } else {
          const isValid = await verifyToken();
          if (isValid) {
            // Apply user/branch theme for admin pages
            applyTheme(resolveTheme(getBranchTheme(), getUserTheme(), null));
            // If just /admin or #admin, go to admin-home
            if (hash === 'admin') {
              setCurrentPage('admin-home');
              window.location.hash = 'admin-home';
            } else {
              // Restore game session for display screen opened in new tab
              if (hash === 'admin-game-display') {
                const saved = localStorage.getItem('ctta_admin_game_session');
                if (saved) {
                  try { setGameSession(JSON.parse(saved)); } catch(e) {}
                }
              }
              setCurrentPage(hash);
            }
          } else {
            removeToken();
            setCurrentPage('admin-login');
            window.location.hash = 'admin-login';
          }
        }
      } else if (hash === 'game-play') {
        // Participant refreshed on game-play — restore session code from localStorage
        const savedCode = localStorage.getItem('ctta_last_game_code');
        if (savedCode) {
          setGameCode(savedCode);
          setCurrentPage('game-play');
        } else {
          // No saved code, send to join screen
          setCurrentPage('game-join');
          window.location.hash = 'game-join';
        }
      } else if (hash) {
        // Regular page navigation
        setCurrentPage(hash);
      }
    };

    // Check on mount
    checkAdminAccess();

    // Listen for hash changes
    const handleHashChange = () => {
      checkAdminAccess();
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Check if current page is an admin page
  const isAdminPage = currentPage.startsWith('admin-') && currentPage !== 'admin-login';

  // Check if current page is a game page
  const isGamePage = currentPage.startsWith('game-');

  // Render game pages (standalone, no layout)
  if (isGamePage) {
    return (
      <div className="App">
        {currentPage === 'game-join' && sitePagesLoaded && sitePages.join_game?.enabled && <GameJoin onJoin={handleGameJoin} />}
        {currentPage === 'game-join' && sitePagesLoaded && !sitePages.join_game?.enabled && (
          <div className="seasonal-page-disabled">Game joining isn't currently open. Check back later!</div>
        )}
        {currentPage === 'game-play' && gameCode && <GamePlay sessionCode={gameCode} />}
      </div>
    );
  }

  // Render admin login page (standalone, no layout)
  if (currentPage === 'admin-login') {
    return (
      <div className="App">
        <AdminLogin onLoginSuccess={() => setCurrentPage('admin-home')} />
      </div>
    );
  }

  // Render game display as full-screen standalone (projector mode — no admin chrome)
  if (currentPage === 'admin-game-display') {
    return (
      <div className="App">
        {gameSession && <AdminGameDisplay session={gameSession} />}
      </div>
    );
  }

  // Render admin pages with AdminLayout
  if (isAdminPage) {
    return (
      <div className="App">
        <AdminLayout
          currentPage={currentPage}
          onNavigate={handleAdminNavigate}
          onLogout={handleLogout}
        >
          {currentPage === 'admin-home' && <AdminHome onNavigate={handleAdminNavigate} />}
          {currentPage === 'admin-calendar' && <AdminCalendar />}
          {currentPage === 'admin-sessions' && <AdminSessionList onSessionSelected={handleSessionCreated} />}
          {currentPage === 'admin-game-create' && <AdminGameCreate onSessionCreated={handleSessionCreated} />}
          {currentPage === 'admin-game-control' && gameSession && <AdminGameControl session={gameSession} onNavigateToDisplay={handleNavigateToDisplay} />}
          {currentPage === 'admin-question-groups' && <AdminQuestionGroups />}
          {currentPage === 'admin-users' && <AdminUserManager />}
          {currentPage === 'admin-branches' && <AdminBranchManager />}
          {currentPage === 'admin-role-permissions' && <AdminRolePermissions />}
          {currentPage === 'admin-faq' && <AdminFAQManager />}
          {currentPage === 'admin-student-registration' && <AdminStudentRegistrationHome onNavigate={handleAdminNavigate} />}
          {currentPage === 'admin-student-registration-setup' && <AdminStudentRegistrationSetup />}
          {currentPage === 'admin-student-registration-import' && <AdminStudentRegistrationImport />}
          {currentPage === 'admin-student-registration-checkin' && <AdminStudentRegistrationCheckIn />}
          {currentPage === 'admin-student-registration-roster' && <AdminStudentRegistrationRoster />}
          {currentPage === 'admin-grade-evaluation' && <AdminGradeEvaluation />}
          {currentPage === 'admin-transfer-requests' && <AdminTransferRequests />}
          {currentPage === 'admin-volunteers' && <AdminVolunteers />}
          {currentPage === 'admin-my-class' && <AdminMyClass />}
          {currentPage === 'admin-email-templates' && <AdminEmailTemplates />}
          {currentPage === 'admin-bulk-email' && <AdminBulkEmail />}
          {currentPage === 'admin-site-pages' && <AdminSitePages />}
          {currentPage === 'admin-contact-us' && <AdminContactUs />}
        </AdminLayout>
      </div>
    );
  }

  // Render public pages with normal layout
  return (
    <div className="App">
      <TopContactBar />
      <header className="header">
        <div className="header-content">
          <div className="logo-section logo-left">
            <img src={logo} alt="CTTA Logo" className="logo" />
          </div>
          <div className="title-section">
            <h1>Central Texas Tamil Academy</h1>
            <h2 className="title-tamil">மத்திய டெக்சாஸ் தமிழ் கழகம்</h2>
          </div>
          <div className="logo-section logo-right">
            <img src={itaLogo} alt="ITA Logo" className="logo" />
          </div>
        </div>
      </header>

      <nav className="navbar">
        <button className="hamburger-menu" onClick={toggleMenu} aria-label="Toggle menu">
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
        <ul className={`nav-menu ${menuOpen ? 'nav-menu-open' : ''}`}>
          <li className="nav-item">
            <a href="#home" className={`nav-link ${currentPage === 'home' ? 'active' : ''}`} onClick={() => handleNavClick('home')}>Home</a>
          </li>
          <li className="nav-item">
            <a href="#calendar" className={`nav-link ${currentPage === 'calendar' ? 'active' : ''}`} onClick={() => handleNavClick('calendar')}>Calendar</a>
          </li>
          <li className="nav-item">
            <a href="#location" className={`nav-link ${currentPage === 'location' ? 'active' : ''}`} onClick={() => handleNavClick('location')}>Location</a>
          </li>
          {sitePages.vasantha_vizha?.enabled && (
            <li className="nav-item">
              <a href="#vasantha-vizha" className={`nav-link ${currentPage === 'vasantha-vizha' ? 'active' : ''}`} onClick={() => handleNavClick('vasantha-vizha')}>{sitePages.vasantha_vizha.nav_label}</a>
            </li>
          )}
          <li className="nav-item">
            <a href="#donation" className={`nav-link ${currentPage === 'donation' ? 'active' : ''}`} onClick={() => handleNavClick('donation')}>Donation</a>
          </li>
          <li className="nav-item">
            <a href="#contactus" className={`nav-link ${currentPage === 'contactus' ? 'active' : ''}`} onClick={() => handleNavClick('contactus')}>Contact Us</a>
          </li>
          <li className="nav-item">
            <a href="#faq" className={`nav-link ${currentPage === 'faq' ? 'active' : ''}`} onClick={() => handleNavClick('faq')}>FAQ</a>
          </li>
          {sitePages.join_game?.enabled && (
            <li className="nav-item">
              <a href="#game-join" className={`nav-link ${currentPage === 'game-join' ? 'active' : ''}`} onClick={() => handleNavClick('game-join')}>{sitePages.join_game.nav_label}</a>
            </li>
          )}
        </ul>
      </nav>

      <main className="main-content">
        {currentPage === 'home' && (
          <>
            <section className="content-section mission-section">
              <h2>Our Mission</h2>
              <p className="tagline">Ensuring our Tamil language and culture passed on to successive generations</p>
              <p>Central Texas Tamil Academy (CTTA) is a non-profit 501c3 organization run by volunteers. We are affiliated with International Tamil Academy and follow their syllabus.</p>

              <h3>School Goals</h3>
              <ul className="goals-list">
                <li>Enable children to speak fluently in Tamil</li>
                <li>Help children to read and write well in Tamil</li>
                <li>Ensure that our Tamil language and Tamil culture pass on to successive generations</li>
                <li>Learn about Tamil culture along with the language</li>
              </ul>
            </section>

            <section className="content-section curriculum-section">
              <h2>Curriculum</h2>
              <p>We follow the syllabus provided by International Tamil Academy to ensure our students receive a comprehensive education in Tamil language and culture.</p>
              <p>The academy uses standardized curriculum materials from the International Tamil Academy to deliver quality Tamil education.</p>
            </section>

            <section className="content-section volunteers-section">
              <h2>Volunteers</h2>
              <p>CTTA is run entirely by volunteers. Join us in our mission to promote Tamil language and culture in Central Texas.</p>
              <p>The organization operates completely through volunteer support and welcomes community participation in their educational mission.</p>
            </section>
          </>
        )}

        {currentPage === 'calendar' && <Calendar />}

        {currentPage === 'location' && <Location />}

        {currentPage === 'vasantha-vizha' && <VasanthaVizha />}

        {currentPage === 'donation' && <Donation />}

        {currentPage === 'contactus' && <ContactUs />}

        {currentPage === 'faq' && <Faq />}

        {currentPage === 'transfer-form' && <TransferAccessGate branchCode={transferBranchCode} />}
      </main>

      <Footer />
    </div>
  );
}

export default App;
